import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function OrderedBySelect({ value, onChange, label = 'Ordered By', required = false }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/ordered-by`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setItems(res.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(false));
  }, []);

  // Sync external value changes
  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    return (item.name || '').toLowerCase().includes(q);
  });

  const handleSelect = (item) => {
    setSearch(item.name || '');
    setOpen(false);
    if (onChange) {
      onChange(item.id, item.name, item.email || '', item.phone || '');
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setOpen(true);
    if (onChange) {
      onChange(null, val, '', '');
    }
  };

  // If items didn't load, fall back to plain text input
  if (!loaded || items.length === 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (onChange) onChange(null, e.target.value, '', '');
          }}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          required={required}
        />
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="Type to search or select..."
          required={required}
          autoComplete="off"
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); setOpen(v => !v); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 px-1 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto">
          {filtered.slice(0, 8).map((item) => (
            <div
              key={item.id}
              onClick={() => handleSelect(item)}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{item.name}</span>
              {item.email && <span className="text-gray-500"> - {item.email}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
